import {useFrontendApisClient} from '~/composables/auth/useFrontendApisClient';
import {readPendingOrgName, clearPendingOrgName} from '~/utils/pendingOrgName';
import {checkOrgNameAvailability} from '~/utils/checkOrgNameAvailability';

// Module-level guard against concurrent bootstrap attempts when multiple
// callers race for useActiveOrg() on first load.
let bootstrapPromise = null;

const deriveOrgNameFromEmail = email => {
  const at = (email || '').indexOf('@');
  const domain = at >= 0 ? email.slice(at + 1) : '';
  const base = (domain.split('.')[0] || '').trim();
  if (!base) return 'Workspace';
  return base.charAt(0).toUpperCase() + base.slice(1);
};

// Walk base, base_1, base_2, ... until /orgs/check-name says it's free.
// Falls back to a random suffix after 99 attempts so we always return.
const findAvailableName = async (apiBaseUrl, base) => {
  const initial = await checkOrgNameAvailability(apiBaseUrl, base);
  if (initial !== 'taken') return base;
  for (let i = 1; i <= 99; i += 1) {
    const candidate = `${base}_${i}`;
    const state = await checkOrgNameAvailability(apiBaseUrl, candidate);
    if (state !== 'taken') return candidate;
  }
  return `${base}_${Math.random().toString(36).slice(2, 6)}`;
};

const bootstrapOrgIfMissing = async ({$propelAuth, client, apiBaseUrl}) => {
  const auth = await $propelAuth.getAuthenticationInfoOrNull();
  if (!auth?.user) return;
  if (auth.orgHelper.getOrgs().length > 0) return;

  // Prefer the company name we stashed during signup; otherwise derive one
  // from the user's email domain. If the chosen name is already taken, append
  // _1, _2, ... until we find an unused variant.
  const stored = readPendingOrgName();
  const baseName = stored || deriveOrgNameFromEmail(auth.user.email);
  const finalName = await findAvailableName(apiBaseUrl, baseName);

  const response = await client.createOrg({name: finalName});
  let createdOk = false;
  response.handle({
    success: () => {
      createdOk = true;
    },
    badRequest: () => {},
    cannotCreateOrgs: () => {},
    cannotUsePersonalDomain: () => {},
    userAlreadyInTooManyOrgs: () => {},
    unauthorized: () => {},
    emailNotConfirmed: () => {},
    unexpectedOrUnhandled: () => {},
  });

  if (createdOk) {
    clearPendingOrgName();
    // Force a refresh so the next read of orgHelper.getOrgs() reflects the
    // new membership (PropelAuth caches auth info in memory).
    await $propelAuth.getAuthenticationInfoOrNull(true);
  } else {
    console.error('Failed to create org', response);
  }
};

export const useActiveOrg = async () => {
  // Capture injection-context dependencies BEFORE any await per the
  // "Vue Hooks Before Await" rule.
  const {$propelAuth} = useNuxtApp();
  const config = useRuntimeConfig().public;
  const client = useFrontendApisClient();
  const apiBaseUrl = config.apiBaseUrl;

  let auth = await $propelAuth.getAuthenticationInfoOrNull();

  // No PropelAuth org yet — bootstrap one before returning so callers (e.g.
  // getAccessToken → apiFetch) can scope tokens to a real orgId.
  if (auth?.user && auth.orgHelper.getOrgs().length === 0) {
    if (!bootstrapPromise) {
      bootstrapPromise = bootstrapOrgIfMissing({$propelAuth, client, apiBaseUrl}).finally(() => {
        bootstrapPromise = null;
      });
    }
    await bootstrapPromise;
    auth = await $propelAuth.getAuthenticationInfoOrNull(true);
  }

  if (!auth?.user) {
    throw new Error('useActiveOrg requires an authenticated user');
  }

  const {orgHelper} = auth;

  const getActiveOrg = () => {
    const defaultOrg = orgHelper.getOrgs()[0].orgId;
    const stored = localStorage.getItem('activeOrg');
    if (stored && !orgHelper.getOrgs().find(org => org.orgId === stored)) {
      localStorage.setItem('activeOrg', defaultOrg);
      return defaultOrg;
    }
    return stored || defaultOrg;
  };

  const setActiveOrg = async orgId => {
    const currentOrgId = getActiveOrg();
    if (currentOrgId === orgId) return;
    localStorage.setItem('activeOrg', orgId);
    window.location.href = '/';
  };

  return {
    getActiveOrg,
    setActiveOrg,
  };
};
